import { useEffect, useState, type ReactNode } from 'react'
import { addMonths, monthName } from './format'

export type Tone = 'plain' | 'amber' | 'red' | 'green' | 'grey'

/** A stat tile: label, big figure, one line under it. */
export function Tile({ label, value, sub, tone = 'plain', badge }: { label: string; value: ReactNode; sub?: ReactNode; tone?: Tone; badge?: ReactNode }) {
  return (
    <div className="rp-tile">
      <div className="rp-tile__head"><span>{label}</span>{badge}</div>
      <span className={'rp-tile__value' + (tone !== 'plain' ? ' rp-tone-' + tone : '')}>{value}</span>
      {sub !== undefined && <span className="rp-tile__sub">{sub}</span>}
    </div>
  )
}

export function Badge({ tone = 'green', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={'rp-badge rp-badge--' + tone}>{children}</span>
}

/** A white card with a title row; `tone` draws a small status dot before the title. */
export function Card({ title, note, right, children, className, tone }: { title?: ReactNode; note?: ReactNode; right?: ReactNode; children: ReactNode; className?: string; tone?: Tone }) {
  const dot = tone && tone !== 'plain' ? <span className={'rp-card__dot rp-card__dot--' + tone} /> : null
  return (
    <section className={'rp-card' + (className ? ' ' + className : '')}>
      {(title || note || right) && (
        <div className="rp-card__head">
          <div><h2>{dot}{title}</h2>{note && <span className="rp-card__note">{note}</span>}</div>
          {right && <div className="rp-card__right">{right}</div>}
        </div>
      )}
      {children}
    </section>
  )
}

export interface Col<T> { key: string; label: ReactNode; align?: 'left' | 'right'; render?: (row: T, i: number) => ReactNode; total?: ReactNode }
/** A grid table: `cols` sets the grid-template-columns; each Col renders one cell. `total` cells draw the bold last row. */
export function Table<T extends object>({ cols, rows, columns, empty, total, rowKey, onRow, loading }: {
  cols: Col<T>[]; rows: T[]; columns: string; empty?: ReactNode; total?: boolean; rowKey?: (r: T, i: number) => string; onRow?: (r: T) => void; loading?: boolean
}) {
  const style = { gridTemplateColumns: columns }
  return (
    <div className="rp-table" role="table">
      <div className="rp-table__head" style={style} role="row">
        {cols.map((c) => <span key={c.key} role="columnheader" className={c.align === 'right' ? 'r' : ''}>{c.label}</span>)}
      </div>
      {loading && rows.length === 0 && [0, 1, 2].map((i) => <div key={i} className="rp-table__row rp-skel" style={style}>{cols.map((c) => <span key={c.key} />)}</div>)}
      {!loading && rows.length === 0 && <div className="rp-table__empty">{empty ?? 'Nothing here yet'}</div>}
      {rows.map((r, i) => (
        <div key={rowKey ? rowKey(r, i) : i} className={'rp-table__row' + (onRow ? ' rp-table__row--link' : '')} style={style} role="row" onClick={onRow ? () => onRow(r) : undefined}>
          {cols.map((c) => <span key={c.key} role="cell" className={c.align === 'right' ? 'r' : ''}>{c.render ? c.render(r, i) : String((r as Record<string, unknown>)[c.key] ?? '')}</span>)}
        </div>
      ))}
      {total && rows.length > 0 && (
        <div className="rp-table__row rp-table__total" style={style} role="row">
          {cols.map((c) => <span key={c.key} role="cell" className={c.align === 'right' ? 'r' : ''}>{c.total ?? ''}</span>)}
        </div>
      )}
    </div>
  )
}

/** Previous / month name / next. */
export function MonthPicker({ month, onChange, min }: { month: string; onChange: (m: string) => void; min?: string }) {
  const today = new Date().toISOString().slice(0, 7)
  return (
    <div className="rp-month">
      <button type="button" aria-label="Previous month" disabled={!!min && month <= min} onClick={() => onChange(addMonths(month, -1))}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      </button>
      <span>{monthName(month)}</span>
      <button type="button" aria-label="Next month" disabled={month >= today} onClick={() => onChange(addMonths(month, 1))}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
      </button>
    </div>
  )
}

export function Button({ children, onClick, primary, disabled, small, title, type = 'button' }: { children: ReactNode; onClick?: () => void; primary?: boolean; disabled?: boolean; small?: boolean; title?: string; type?: 'button' | 'submit' }) {
  return <button type={type} className={'rp-btn' + (primary ? ' rp-btn--primary' : '') + (small ? ' rp-btn--small' : '')} onClick={onClick} disabled={disabled} title={title}>{children}</button>
}

/** A button that runs an async action and shows "…" while it does. */
export function AsyncButton({ children, run, primary, small, disabled, doneLabel }: { children: ReactNode; run: () => Promise<unknown>; primary?: boolean; small?: boolean; disabled?: boolean; doneLabel?: string }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  useEffect(() => { if (!msg) return; const t = setTimeout(() => setMsg(''), 3500); return () => clearTimeout(t) }, [msg])
  return (
    <Button primary={primary} small={small} disabled={disabled || busy} onClick={async () => { setBusy(true); setMsg(''); try { await run(); if (doneLabel) setMsg(doneLabel) } catch (e) { setMsg((e as Error).message) } finally { setBusy(false) } }}>
      {busy ? 'One moment…' : msg || children}
    </Button>
  )
}

export function Empty({ children }: { children: ReactNode }) { return <div className="rp-empty">{children}</div> }

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="rp-field"><span className="rp-field__label">{label}</span>{children}{hint && <span className="rp-field__hint">{hint}</span>}</label>
}

/** Four-digit owner ID field, resolved against the owners the server lists. */
export function OwnerId({ value, onChange, owners }: { value: string; onChange: (v: string) => void; owners: { employee_id: string; name: string }[] }) {
  const match = value.length === 4 ? owners.find((o) => o.employee_id === value) : undefined
  return (
    <Field label="Owner ID" hint={value.length === 4 ? (match ? `Signed by ${match.name}` : 'Not an owner ID') : 'Four digits; an owner signs every payout and settlement'}>
      <input className="rp-input" inputMode="numeric" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="••••" style={{ letterSpacing: '.2em', width: 120 }} />
    </Field>
  )
}

export function Note({ children }: { children: ReactNode }) { return <p className="rp-note">{children}</p> }
